'use client';

import { useEffect, useRef } from 'react';

import { useCart } from '../hooks/useCart';
import { useDecisions } from '../hooks/useDecisions';
import { useLoyaltyContext } from '../hooks/useLoyaltyContext';
import { usePageLayout } from '../hooks/usePageLayout';
import { useConsentCategory } from '../scripts/hooks/useConsentCategory';
import { buildExtras, buildPetsmartMedalliaContextFromCartUI } from './buildPetsmartMedalliaContext';
import { useGetActiveOptimizelyCampaigns } from './useGetActiveOptimizelyCampaigns';

export function MedalliaCartSync() {
    const { cart } = useCart();
    const { memberPoints } = useLoyaltyContext();
    const { user, userData } = usePageLayout();
    const hasC0002 = useConsentCategory('C0002');
    const ffMedalliaCustom = useDecisions('medallia_custom');

    const lastPublishedRef = useRef<string | null>(null);

    const optimizelyCampaigns = useGetActiveOptimizelyCampaigns(hasC0002);

    useEffect(() => {
        if (!hasC0002 || !ffMedalliaCustom || !cart) {
            delete window.petsmartMedalliaContext;
            lastPublishedRef.current = null;
            return;
        }

        const extras = buildExtras({
            user,
            userData,
            memberPoints,
            optimizelyCampaigns,
        });

        const next = buildPetsmartMedalliaContextFromCartUI(cart, extras);
        const nextSerialized = JSON.stringify(next);

        window.petsmartMedalliaContext = next;

        if (nextSerialized === lastPublishedRef.current) {
            return () => {
                delete window.petsmartMedalliaContext;
            };
        }

        // Payload changed (or never sent). Notify the SDK, but only stamp lastPublishedRef
        // when updatePageView actually fires — if the SDK is absent the ref stays unstamped
        // so a neb_OnsiteLoaded retry or the next re-render will still attempt the call.
        const notify = () => {
            if (window.KAMPYLE_ONSITE_SDK) {
                lastPublishedRef.current = nextSerialized;
                window.KAMPYLE_ONSITE_SDK.updatePageView();
            }
        };

        if (window.KAMPYLE_ONSITE_SDK) {
            notify();
            return () => {
                delete window.petsmartMedalliaContext;
            };
        }

        // SDK not yet loaded — register a one-time listener. Context is already on window so
        // the SDK reads custom parameters as soon as it calls updatePageView. Matches the
        // pattern used by useMedalliaForm and useMedalliaFormV2.
        window.addEventListener('neb_OnsiteLoaded', notify, { once: true });
        return () => {
            window.removeEventListener('neb_OnsiteLoaded', notify);
            delete window.petsmartMedalliaContext;
            // lastPublishedRef intentionally kept: prevents redundant updatePageView calls
            // when deps change but the resulting payload is identical
        };
    }, [cart, hasC0002, ffMedalliaCustom, user, userData, memberPoints, optimizelyCampaigns]);

    return null;
}
